import { useTheme } from '@mui/material/styles';

// Caliber-specific cartridge silhouettes.
//
// All silhouettes share viewBox 0 0 200 56, bullets pointing RIGHT, vertical
// centerline y=28. Cartridges are at roughly relative real-world scale, so a
// .308 dwarfs a .380 at the same render height — a useful size-shopping cue.
//
// Each definition is two paths (case + bullet, or brass + hull for shotshell)
// plus an optional thin "detail" path for the case-mouth / shoulder lines.

type CartridgeDef = {
  case: string;
  bullet: string;
  detail?: string;
  label: string;
  isShell?: boolean;
};

type AmmoCategory = 'handgun' | 'rifle' | 'rimfire' | 'shotgun';

/** Which specific silhouettes belong to which ammo category. */
const HANDGUN_SPECIFIC = new Set(['9mm', '380', '40sw', '45acp']);
const RIFLE_SPECIFIC = new Set(['556', '223', '308', '65cm', '300blk', '762x39']);

/**
 * Normalize the taxonomy-derived ammoType (handgun/rifle/rimfire/shotgun) — the
 * authoritative category. It's the ONLY reliable signal: the caliber string
 * can't disambiguate (e.g. .22 TCM is handgun, .22 Hornet/.22-250 are rifle,
 * .224 Valkyrie is rifle). May arrive as a string or single-element array.
 */
function normAmmoType(ammoType?: string | string[] | null): AmmoCategory | null {
  const a = Array.isArray(ammoType) ? ammoType[0] : ammoType;
  const s = String(a || '').toLowerCase();
  return s === 'handgun' || s === 'rifle' || s === 'rimfire' || s === 'shotgun' ? s : null;
}

/** Specific, recognizable silhouette for a common caliber — else null. */
function specificFamily(c: string): string | null {
  if (c.includes('12ga') || c.includes('12gauge') || c.includes('12g')) return '12ga';
  if (c.includes('.22lr') || c.includes('22lr')) return '22lr';
  if (c.includes('.380') || c.includes('380acp')) return '380';
  if (c.includes('.40s&w') || c.includes('40s&w') || c.includes('.40sw') || c.includes('40sw'))
    return '40sw';
  if (c.includes('.45acp') || c.includes('45acp') || c.includes('.45auto')) return '45acp';
  if (c.includes('9mm') || c.includes('9×19') || c.includes('9x19')) return '9mm';
  if (c.includes('5.56') || c.includes('556')) return '556';
  if (c.includes('.223') || c.includes('223rem')) return '223';
  if (c.includes('.308') || c.includes('308win') || c.includes('7.62nato') || c.includes('7.62x51'))
    return '308';
  if (c.includes('6.5cm') || c.includes('6.5creedmoor')) return '65cm';
  if (c.includes('300blk') || c.includes('300blackout') || c.includes('.300blk')) return '300blk';
  if (c.includes('7.62×39') || c.includes('7.62x39')) return '762x39';
  return null;
}

// Last-resort shotgun detection from the caliber string (gauge / bore / .410),
// only used when no ammoType is supplied.
const SHOTGUN_RX = /\bgauge\b|\d+\s*ga\b|\bbore\b|\.410|410bore/;

// Last-resort handgun/revolver detection from the caliber string — common
// revolver/pistol calibers that start with "." (so the dotted→rifle fallback
// below otherwise misfires them to a rifle silhouette: .38 Special, .357
// Magnum, .44 Mag, etc.). Only consulted when no ammoType is supplied; the
// taxonomy-derived ammoType stays authoritative. Tested against the
// space-stripped, lowercased caliber string.
const HANDGUN_RX =
  /\.?357(magnum|mag|sig)|\.?38(special|spl|super)|\.?44(magnum|mag|special|spl)|\.?45(colt|lc)|\.?454|\.?460s|\.?500s|\.?41(magnum|mag)|10mm/;

/**
 * Resolve a caliber to one of our defined silhouettes.
 *
 * Strategy (correctness > specificity):
 *  1. Prefer the taxonomy-derived `ammoType` to pick the CATEGORY (the only
 *     reliable signal). Within the category, use the specific silhouette when
 *     the caliber is one we draw, else the category's generic silhouette.
 *  2. With no ammoType, use a recognized specific silhouette if any.
 *  3. With neither, fall back conservatively (shotgun by string, then a
 *     bottleneck/straight-wall guess). Surfaces that render arbitrary calibers
 *     (search results, product detail) always pass ammoType, so step 1 wins.
 */
export function cartridgeFamily(
  caliber: string | null | undefined,
  ammoType?: string | string[] | null
): string {
  const c = String(caliber || '')
    .toLowerCase()
    .replace(/\s+/g, '');
  const specific = specificFamily(c);
  const cat = normAmmoType(ammoType);

  if (cat === 'shotgun') return specific === '12ga' ? '12ga' : 'shell';
  if (cat === 'rimfire') return '22lr';
  if (cat === 'handgun') return specific && HANDGUN_SPECIFIC.has(specific) ? specific : 'pistol';
  if (cat === 'rifle') return specific && RIFLE_SPECIFIC.has(specific) ? specific : 'rifle';

  if (specific) return specific;
  if (SHOTGUN_RX.test(String(caliber || '').toLowerCase())) return 'shell';
  if (HANDGUN_RX.test(c)) return 'pistol';
  // No ammoType and no recognizable caliber: dotted/numeric leans rifle,
  // bare words lean pistol. (Main surfaces pass ammoType, so this is rare.)
  return /^\d|^\./.test(c) ? 'rifle' : 'pistol';
}

const DEFS: Record<string, CartridgeDef> = {
  '9mm': {
    case: 'M 60,15 L 67,15 L 67,17 L 115,17 L 115,39 L 67,39 L 67,41 L 60,41 Z',
    bullet: 'M 115,17 Q 138,17 145,22 Q 150,28 145,34 Q 138,39 115,39 Z',
    detail: 'M 115,17 L 115,39',
    label: '9mm Luger',
  },
  '380': {
    case: 'M 70,16 L 77,16 L 77,18 L 115,18 L 115,38 L 77,38 L 77,40 L 70,40 Z',
    bullet: 'M 115,18 Q 135,18 142,23 Q 146,28 142,33 Q 135,38 115,38 Z',
    detail: 'M 115,18 L 115,38',
    label: '.380 ACP',
  },
  '40sw': {
    case: 'M 60,14 L 67,14 L 67,16 L 115,16 L 115,40 L 67,40 L 67,42 L 60,42 Z',
    bullet: 'M 115,16 Q 138,16 145,22 Q 150,28 145,34 Q 138,40 115,40 Z',
    detail: 'M 115,16 L 115,40',
    label: '.40 S&W',
  },
  '45acp': {
    case: 'M 50,12 L 60,12 L 60,14 L 115,14 L 115,42 L 60,42 L 60,44 L 50,44 Z',
    bullet: 'M 115,14 Q 140,14 148,22 Q 156,28 148,34 Q 140,42 115,42 Z',
    detail: 'M 115,14 L 115,42',
    label: '.45 ACP',
  },
  '22lr': {
    case: 'M 73,18 L 84,18 L 84,22 L 115,22 L 115,34 L 84,34 L 84,38 L 73,38 Z',
    bullet: 'M 115,22 Q 130,22 133,25 Q 136,28 133,31 Q 130,34 115,34 Z',
    detail: 'M 115,22 L 115,34',
    label: '.22 LR',
  },
  '556': {
    case: 'M 20,16 L 28,16 L 28,18 L 130,18 L 140,23 L 155,23 L 155,33 L 140,33 L 130,38 L 28,38 L 28,40 L 20,40 Z',
    bullet: 'M 155,23 Q 175,23 182,26 L 187,28 L 182,30 Q 175,33 155,33 Z',
    detail: 'M 155,23 L 155,33  M 130,18 L 140,23  M 130,38 L 140,33',
    label: '5.56×45 NATO',
  },
  '223': {
    case: 'M 20,16 L 28,16 L 28,18 L 130,18 L 140,23 L 155,23 L 155,33 L 140,33 L 130,38 L 28,38 L 28,40 L 20,40 Z',
    bullet: 'M 155,23 Q 175,23 182,26 L 187,28 L 182,30 Q 175,33 155,33 Z',
    detail: 'M 155,23 L 155,33  M 130,18 L 140,23  M 130,38 L 140,33',
    label: '.223 Rem',
  },
  '308': {
    case: 'M 12,10 L 20,10 L 20,12 L 130,12 L 145,22 L 160,22 L 160,34 L 145,34 L 130,44 L 20,44 L 20,46 L 12,46 Z',
    bullet: 'M 160,22 Q 178,22 185,26 L 189,28 L 185,30 Q 178,34 160,34 Z',
    detail: 'M 160,22 L 160,34  M 130,12 L 145,22  M 130,44 L 145,34',
    label: '.308 Win',
  },
  '65cm': {
    case: 'M 12,10 L 20,10 L 20,12 L 128,12 L 142,23 L 158,23 L 158,33 L 142,33 L 128,44 L 20,44 L 20,46 L 12,46 Z',
    bullet: 'M 158,23 Q 182,23 188,26 L 192,28 L 188,30 Q 182,33 158,33 Z',
    detail: 'M 158,23 L 158,33  M 128,12 L 142,23  M 128,44 L 142,33',
    label: '6.5 Creedmoor',
  },
  '300blk': {
    case: 'M 35,16 L 43,16 L 43,18 L 115,18 L 122,21 L 135,21 L 135,35 L 122,35 L 115,38 L 43,38 L 43,40 L 35,40 Z',
    bullet: 'M 135,21 Q 158,21 163,25 L 167,28 L 163,31 Q 158,35 135,35 Z',
    detail: 'M 135,21 L 135,35  M 115,18 L 122,21  M 115,38 L 122,35',
    label: '.300 Blackout',
  },
  '762x39': {
    case: 'M 22,12 L 30,12 L 30,14 L 128,18 L 138,21 L 152,21 L 152,35 L 138,35 L 128,38 L 30,42 L 30,44 L 22,44 Z',
    bullet: 'M 152,21 Q 172,21 178,25 L 182,28 L 178,31 Q 172,35 152,35 Z',
    detail: 'M 152,21 L 152,35',
    label: '7.62×39',
  },
  '12ga': {
    case: 'M 18,7 L 28,7 L 28,9 L 56,9 L 56,47 L 28,47 L 28,49 L 18,49 Z',
    bullet:
      'M 56,9 L 162,9 L 168,11 L 172,15 L 174,18 L 174,38 L 172,41 L 168,45 L 162,47 L 56,47 Z',
    detail: 'M 56,9 L 56,47  M 168,15 L 174,15  M 168,41 L 174,41',
    label: '12 Gauge',
    isShell: true,
  },
  rifle: {
    case: 'M 20,16 L 28,16 L 28,18 L 130,18 L 140,23 L 155,23 L 155,33 L 140,33 L 130,38 L 28,38 L 28,40 L 20,40 Z',
    bullet: 'M 155,23 Q 175,23 182,26 L 187,28 L 182,30 Q 175,33 155,33 Z',
    detail: 'M 155,23 L 155,33',
    label: 'rifle round',
  },
  // Generic straight-wall pistol/revolver case — used for handgun calibers we
  // don't draw specifically (.38 Special, .357, .44 Mag, 10mm, etc.).
  pistol: {
    case: 'M 58,15 L 66,15 L 66,17 L 118,17 L 118,39 L 66,39 L 66,41 L 58,41 Z',
    bullet: 'M 118,17 Q 140,17 147,22 Q 152,28 147,34 Q 140,39 118,39 Z',
    detail: 'M 118,17 L 118,39',
    label: 'pistol cartridge',
  },
  // Generic shotshell — used for gauges other than 12 (20, 16, 28, 10) + .410.
  shell: {
    case: 'M 24,11 L 34,11 L 34,13 L 60,13 L 60,43 L 34,43 L 34,45 L 24,45 Z',
    bullet:
      'M 60,13 L 156,13 L 162,15 L 166,19 L 168,22 L 168,34 L 166,37 L 162,41 L 156,43 L 60,43 Z',
    detail: 'M 60,13 L 60,43  M 162,19 L 168,19  M 162,37 L 168,37',
    label: 'shotshell',
    isShell: true,
  },
};

export interface CartridgeProps {
  caliber: string | null | undefined;
  /**
   * Taxonomy-derived ammo category (handgun/rifle/rimfire/shotgun). When
   * available it authoritatively picks the silhouette family — pass it on any
   * surface that renders arbitrary calibers (search results, product detail).
   */
  ammoType?: string | string[] | null;
  /** Render height in px; width auto-derives from the 200×56 viewBox. */
  height?: number;
  /** Tint the bullet with the brand accent instead of the muted tone. */
  accent?: boolean;
  /** Show the resolved caliber label beneath the silhouette. */
  showLabel?: boolean;
  /** Treat purely as decoration (hide from the accessibility tree). */
  decorative?: boolean;
  style?: React.CSSProperties;
}

/** Render a cartridge silhouette for a given caliber. */
export function Cartridge({
  caliber,
  ammoType,
  height = 56,
  accent = false,
  showLabel = false,
  decorative = false,
  style,
}: CartridgeProps) {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const def: CartridgeDef =
    DEFS[cartridgeFamily(caliber, ammoType)] ?? (DEFS['9mm'] as CartridgeDef);

  const caseFill = def.isShell ? t.dim : t.lineHi;
  const bulletFill = def.isShell ? t.lineHi : accent ? t.accent : t.dim;
  const stroke = t.dim;
  const aspect = 200 / 56;

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        ...style,
      }}
    >
      <svg
        height={height}
        width={height * aspect}
        viewBox="0 0 200 56"
        style={{ display: 'block', overflow: 'visible' }}
        {...(decorative
          ? { 'aria-hidden': true, focusable: false }
          : { role: 'img', 'aria-label': `${def.label} cartridge` })}
      >
        <path
          d={def.case}
          fill={caseFill}
          stroke={stroke}
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        <path
          d={def.bullet}
          fill={bulletFill}
          stroke={stroke}
          strokeWidth="0.6"
          strokeLinejoin="round"
        />
        {def.detail && (
          <path d={def.detail} fill="none" stroke={stroke} strokeWidth="0.5" opacity="0.55" />
        )}
      </svg>
      {showLabel && (
        <span
          style={{
            fontFamily: t.monoFont,
            fontSize: 9,
            color: t.dim,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {def.label}
        </span>
      )}
    </span>
  );
}

export default Cartridge;
